import {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
  ChangeEvent,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { EditButton, useVersion } from 'react-admin';
import keyBy from 'lodash/keyBy';
import { PhaseStructureTypeEnum } from '@vatos-pas/common';

import { SnackbarContainer } from 'components/standalone';
import { extractErrorMessage } from 'utils';
import { PermissionsProvider } from 'providers/permissionsProvider';
import {
  CAN_TOGGLE_SUBDIVISION_BUMPOUT_TIMESHEET_FLOW,
  CAN_SEE_SP_POTENTIAL_MATCHES,
  CAN_EDIT_SP_POTENTIAL_MATCHES,
  CAN_VIEW_SUBDIVISION_GEOFENCING_THRESHOLD,
  CAN_EDIT_SUBDIVISION_GEOFENCING_THRESHOLD,
} from 'providers/permissions';
import {
  SpMatchesDefaultModal,
  SpMatchesDefaultTableList,
  SpMatchesDefaultTableRow,
  SpMatchesError,
} from 'components/SpMatchesDefaultTable';
import {
  SubDivisionContainer,
  SubDivisionFormValues,
  SupervisorOption,
} from '../containers';

interface SubdivisionSupervisorRelation {
  id: string;
  userId: string;
}
import { SubDivisionForm } from '../components/SubDivisionForm';
import { validateSubdivisionCreation } from './subDivisionValidation';
import { MOBILE_TOOLBAR_TOTAL } from 'components/standalone/FormToolbar';

const SubDivisionsEditInner = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { notify } = SnackbarContainer.useContainer();
  const { hasPermission } = PermissionsProvider.useContainer();
  const {
    selectedSubdivision,
    isLoading,
    subdivisionLoaded,
    subdivisionError,
    formLoading,
    loadSubdivisionById,
    updateSubdivisionWithSupervisors,
    supervisors,
    builderSupervisors,
    supervisorsLoading,
    selectedRegion,
    regionLoading,
    loadRegion,
    loadSupervisors,
    loadBuilderSupervisors,
    spSubdivisions,
    spSubdivisionsLoading,
    loadSpSubdivisions,
    createSpSubdivision,
    updateSpSubdivisionActive,
    clearSupervisors,
  } = SubDivisionContainer.useContainer();

  // BACKWARDS COMPAT: useVersion triggers re-fetch when react-admin
  // toolbar refresh button is clicked. Required while using custom
  // containers instead of react-admin's built-in data hooks.
  const version = useVersion();

  // Local state
  const [selectedSupervisors, setSelectedSupervisors] = useState<
    SupervisorOption[]
  >([]);
  const [selectedBuilderSupervisors, setSelectedBuilderSupervisors] = useState<
    SupervisorOption[]
  >([]);
  const [supervisorError, setSupervisorError] = useState<string>('');
  const [showEditSpModal, setShowEditSpModal] = useState(false);
  const [spError, setSpError] = useState<SpMatchesError | null>(null);

  // Refs for tracking initial load
  const hasLoadedInitialSupervisors = useRef(false);
  const hasLoadedInitialBuilderSupervisors = useRef(false);
  const initialRegionId = useRef<string | undefined>(undefined);

  const subdivisionRegionId = selectedSubdivision?.regionId;
  const subdivisionId = selectedSubdivision?.id;

  const supervisorsDictionary = useMemo(
    () => keyBy(supervisors, 'id'),
    [supervisors],
  );

  const builderSupervisorsDictionary = useMemo(
    () => keyBy(builderSupervisors, 'id'),
    [builderSupervisors],
  );

  // Load subdivision data (SWR: triggers useGetOne which uses Redux cache)
  useEffect(() => {
    if (!id) return;
    loadSubdivisionById(id);
  }, [id, version, loadSubdivisionById]);

  useEffect(() => {
    if (subdivisionError || (subdivisionLoaded && !selectedSubdivision)) {
      notify('Subdivision not found', 'error');
      history.push('/subdivision');
    }
  }, [
    subdivisionError,
    subdivisionLoaded,
    selectedSubdivision,
    notify,
    history,
  ]);

  // Store initial region ID on first load
  useEffect(() => {
    if (!subdivisionRegionId) return;
    if (!initialRegionId.current) {
      initialRegionId.current = subdivisionRegionId;
    }
  }, [subdivisionRegionId]);

  // Load region-related secondary data when regionId changes
  useEffect(() => {
    if (!subdivisionRegionId) return;
    loadRegion(subdivisionRegionId);
    loadSupervisors(subdivisionRegionId);
    loadBuilderSupervisors(subdivisionRegionId);
  }, [
    subdivisionRegionId,
    loadRegion,
    loadSupervisors,
    loadBuilderSupervisors,
  ]);

  // Load SP subdivisions when subdivision id changes
  useEffect(() => {
    if (!subdivisionId) return;
    loadSpSubdivisions(subdivisionId);
  }, [subdivisionId, loadSpSubdivisions]);

  // Map initial supervisors once loaded
  useEffect(() => {
    if (hasLoadedInitialSupervisors.current) return;
    if (!Object.keys(supervisorsDictionary).length) return;
    if (!selectedSubdivision) return;

    if (selectedSubdivision.subdivisionSupervisors?.length) {
      const mapped = selectedSubdivision.subdivisionSupervisors
        .map((item: SubdivisionSupervisorRelation) => {
          const user = supervisorsDictionary[item.userId];
          if (!user) return null;
          return {
            name: `${user.firstName} ${user.lastName}`,
            id: item.userId,
          };
        })
        .filter(Boolean) as SupervisorOption[];

      setSelectedSupervisors(mapped);
    }

    hasLoadedInitialSupervisors.current = true;
  }, [selectedSubdivision, supervisorsDictionary]);

  // Map initial builder supervisors once loaded
  useEffect(() => {
    if (hasLoadedInitialBuilderSupervisors.current) return;
    if (!Object.keys(builderSupervisorsDictionary).length) return;
    if (!selectedSubdivision) return;

    if (selectedSubdivision.subdivisionBuilderSupervisors?.length) {
      const mapped = selectedSubdivision.subdivisionBuilderSupervisors
        .map((item: SubdivisionSupervisorRelation) => {
          const user = builderSupervisorsDictionary[item.userId];
          if (!user) return null;
          return {
            name: `${user.firstName} ${user.lastName}`,
            id: item.userId,
          };
        })
        .filter(Boolean) as SupervisorOption[];

      setSelectedBuilderSupervisors(mapped);
    }

    hasLoadedInitialBuilderSupervisors.current = true;
  }, [selectedSubdivision, builderSupervisorsDictionary]);

  const handleSupervisorsChange = useCallback(
    (newSupervisors: SupervisorOption[]) => {
      setSelectedSupervisors(newSupervisors);
      if (supervisors.length > 0 && newSupervisors.length === 0) {
        setSupervisorError('Please select at least one Supervisor.');
      } else {
        setSupervisorError('');
      }
    },
    [supervisors],
  );

  const handleRegionChange = useCallback(
    async (regionId: string) => {
      if (!regionId) return;

      // Skip if same as initial region (initial load)
      if (
        regionId === initialRegionId.current &&
        !hasLoadedInitialSupervisors.current
      ) {
        return;
      }

      // Clear supervisor selections when region changes
      clearSupervisors();
      setSelectedSupervisors([]);
      setSelectedBuilderSupervisors([]);

      // Reset initial load flags since region changed
      hasLoadedInitialSupervisors.current = true;
      hasLoadedInitialBuilderSupervisors.current = true;

      // Load new data
      await loadRegion(regionId);
      await loadSupervisors(regionId);
      await loadBuilderSupervisors(regionId);
    },
    [clearSupervisors, loadRegion, loadSupervisors, loadBuilderSupervisors],
  );

  const handleSubmit = useCallback(
    async (values: SubDivisionFormValues) => {
      if (!selectedSubdivision) return;

      // Validate using the same validation function
      const validationPayload = {
        ...values,
        phaseStructureType:
          selectedSubdivision.phaseStructureType ||
          values.phaseStructureType ||
          PhaseStructureTypeEnum.DrywallAndPaint,
      };

      const validationErrors = await validateSubdivisionCreation(
        validationPayload,
        { region: selectedRegion },
      );
      if (Object.keys(validationErrors).length > 0) {
        notify(
          'All Cost Structure fields are required if Override Cost is checked',
          'warning',
        );
        return;
      }

      if (selectedSupervisors.length === 0) {
        setSupervisorError('Please select at least one Supervisor.');
        notify('Please select at least one Supervisor', 'error');
        return;
      }

      try {
        // Single transactional request: fields + desired supervisor sets.
        // Selections are sent as picked; the API refuses users that are not
        // active Supervisors / BuilderSupervisors of the (new) region instead
        // of the client silently dropping what it failed to load.
        await updateSubdivisionWithSupervisors(
          id,
          values,
          selectedSupervisors.map(s => s.id),
          selectedBuilderSupervisors.map(s => s.id),
        );

        notify('Element updated');
        history.push('/subdivision');
      } catch (error: unknown) {
        console.error('Subdivision update failed:', error);
        notify(
          extractErrorMessage(error, 'Error while updating the subdivision'),
          'error',
        );
      }
    },
    [
      id,
      selectedSubdivision,
      selectedRegion,
      updateSubdivisionWithSupervisors,
      selectedSupervisors,
      selectedBuilderSupervisors,
      notify,
      history,
    ],
  );

  const handleGoBack = useCallback(() => {
    history.push('/subdivision');
  }, [history]);

  // SP Potential Matches handlers
  const handleUpdateActiveFlag = useCallback(
    async (event: ChangeEvent<HTMLInputElement>, spId: string) => {
      const spItem = spSubdivisions.find(item => item.id === spId);
      if (!spItem) return;

      try {
        await updateSpSubdivisionActive(spId, event.target.checked, spItem);
        notify('SupplyPro subdivision name updated!', 'success');
      } catch {
        notify('Failed to update SupplyPro subdivision name.', 'error');
      }
    },
    [spSubdivisions, updateSpSubdivisionActive, notify],
  );

  const handleAddNewSpItem = useCallback(
    async (spName: string, active: boolean) => {
      const spNameTrimmed = spName.trim();

      if (spNameTrimmed.length === 0) {
        setSpError({ message: 'Subdivision name cannot be empty.' });
        return;
      }

      if (!selectedSubdivision) return;

      try {
        const result = await createSpSubdivision(
          selectedSubdivision.id,
          spNameTrimmed,
          active,
          selectedSubdivision.builderId,
        );

        if (result) {
          notify('SupplyPro subdivision name created!', 'success');
          return result;
        } else {
          notify('Failed to add a new SupplyPro subdivision name.', 'error');
        }
      } catch {
        notify('Failed to add a new SupplyPro subdivision name.', 'error');
      }
    },
    [selectedSubdivision, createSpSubdivision, notify],
  );

  // Create SP matches table rows
  const spRows: SpMatchesDefaultTableRow[] | null = useMemo(
    () =>
      spSubdivisions?.map(subdivision => ({
        id: subdivision.id,
        name: subdivision.spName,
        active: subdivision.active,
      })) || null,
    [spSubdivisions],
  );

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedSubdivision) {
    return null;
  }

  return (
    <>
      <Box className={classes.editBox}>
        <Box className={classes.formContainer}>
          <SubDivisionForm
            mode="edit"
            initialValues={selectedSubdivision}
            onSubmit={handleSubmit}
            // Saving with a half-loaded supervisor list would send an
            // incomplete desired set, so the form waits for the pickers.
            loading={formLoading || supervisorsLoading}
            onGoBack={handleGoBack}
            supervisors={supervisors}
            builderSupervisors={builderSupervisors}
            supervisorsLoading={supervisorsLoading}
            selectedSupervisors={selectedSupervisors}
            selectedBuilderSupervisors={selectedBuilderSupervisors}
            onSupervisorsChange={handleSupervisorsChange}
            onBuilderSupervisorsChange={setSelectedBuilderSupervisors}
            supervisorError={supervisorError}
            selectedRegion={selectedRegion}
            regionLoading={regionLoading}
            onRegionChange={handleRegionChange}
            canToggleBumpoutTimesheet={hasPermission(
              CAN_TOGGLE_SUBDIVISION_BUMPOUT_TIMESHEET_FLOW,
            )}
            canViewGeofencingThreshold={hasPermission(
              CAN_VIEW_SUBDIVISION_GEOFENCING_THRESHOLD,
            )}
            canEditGeofencingThreshold={hasPermission(
              CAN_EDIT_SUBDIVISION_GEOFENCING_THRESHOLD,
            )}
          />
        </Box>
      </Box>

      {/* SP Potential Matches Section */}
      {hasPermission(CAN_SEE_SP_POTENTIAL_MATCHES) && (
        <>
          {spSubdivisionsLoading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : (
            <Box className={classes.spMatchesBox}>
              <Typography
                className={classes.spMatchesHeading}
                variant="h6"
                component="h3"
              >
                SupplyPro - Potential Matches
              </Typography>

              <SpMatchesDefaultTableList
                columns={['SupplyPro Potential Matches']}
                rows={spRows}
              />

              {hasPermission(CAN_EDIT_SP_POTENTIAL_MATCHES) && (
                <>
                  <SpMatchesDefaultModal
                    open={showEditSpModal}
                    onClose={() => setShowEditSpModal(false)}
                    error={spError}
                    columns={['SupplyPro Potential Matches']}
                    onUpdateActiveFlag={handleUpdateActiveFlag}
                    onAddNewItem={handleAddNewSpItem}
                    rows={spRows}
                  />

                  <EditButton
                    variant="contained"
                    size="medium"
                    onClick={() => setShowEditSpModal(true)}
                    className={classes.editButton}
                  />
                </>
              )}
            </Box>
          )}
        </>
      )}
    </>
  );
};

export const SubDivisionsEdit = () => {
  return (
    <SubDivisionContainer.Provider>
      <SubDivisionsEditInner />
    </SubDivisionContainer.Provider>
  );
};

const useStyles = makeStyles(theme => ({
  editBox: {
    maxWidth: '1500px',
  },
  formContainer: {
    position: 'relative',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  spMatchesBox: {
    maxWidth: '500px',
    marginTop: '24px',
    [theme.breakpoints.down('sm')]: {
      paddingBottom: MOBILE_TOOLBAR_TOTAL + 20,
    },
  },
  spMatchesHeading: {
    marginBottom: '16px',
  },
  editButton: {
    marginTop: '24px',
  },
}));

export default SubDivisionsEdit;
